import React, { createRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "clutch/src/Button/Button.jsx";
import type { DeepReadonly } from "ts-essentials";

import type { RouteState } from "@/__main__/router.mjs";
import router, { updateRoute } from "@/__main__/router.mjs";
import { APP_SCROLLER, GAME_BOX_ICONS } from "@/app/constants.mjs";
import Account from "@/inline-assets/account.svg";
import ExclamationIcon from "@/inline-assets/exclamation-mark.svg";
import LoadingSpinner from "@/inline-assets/loading-spinner.svg";
import ErrorComponent from "@/shared/ErrorComponent.jsx";
import { Match } from "@/shared/Match.jsx";
import { MatchTileAd } from "@/shared/MatchTileAd.jsx";
import PageHeader from "@/shared/PageHeader.jsx";
import profileRefs from "@/shared/Profile.refs.jsx";
import {
  MatchesNotFoundContainer,
  MatchListEmptyContainer,
  MatchListFooter,
  MatchSeparatorContainer,
  MatchTileContainer,
  ProfileColumn,
  ProfileContainer,
} from "@/shared/Profile.style.jsx";
import useGetMatchTileAdIndex from "@/shared/use-get-match-tile-ad-index.mjs";
import { classNames } from "@/util/class-names.mjs";
import debounce from "@/util/debounce.mjs";
import { devError, devWarn } from "@/util/dev.mjs";
import { useGameInfo, useGameSymbol } from "@/util/game-route.mjs";
import globals from "@/util/global-whitelist.mjs";
import { rewritePathname } from "@/util/mod-pathname.mjs";
import { useIsLoaded, useRoute } from "@/util/router-hooks.mjs";

type MatchListProps = {
  children: React.ReactNode;
  matchList:
    | Error
    | Array<{
        [key: string]: unknown;
      }>
    | DeepReadonly<Array<{ [key: string]: unknown }>>;
};
type MatchTileWrapperProps = {
  match: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  matchRoute?: string;
  isLive?: boolean;
  children: React.ReactNode;
  height?: number;
  handleMouseEvents?: boolean;
};
type MatchTileProps = {
  id?: string;
  handleMouseEvents?: boolean;
} & MatchTileWrapperProps;

type MatchSeparatorTileProps = {
  Icon?: React.FC;
  label?: string;
};
type ShowMoreFooterProps = {
  hasMore: boolean;
  setShowMore: () => void;
};
type ProfileProps = {
  children: React.ReactNode;
};

const MatchListLoading = () => {
  return (
    <MatchListEmptyContainer>
      <LoadingSpinner />
    </MatchListEmptyContainer>
  );
};

const MatchListError = () => {
  return (
    <MatchListEmptyContainer>
      <ExclamationIcon width={40} height={40} />
    </MatchListEmptyContainer>
  );
};

export function MatchList({ children, matchList }: MatchListProps) {
  const gameSymbol = useGameSymbol();

  const matchListError = matchList instanceof Error ? matchList : null;
  const isLoaded = useIsLoaded();
  const hasMatches = Boolean(Object.keys(matchList ?? {}).length);
  const hasLoaded = isLoaded || hasMatches;

  // This key is needed because we expect it to be inserted in a list,
  // it just has to not collide with anything else in the list.
  const element = <MatchTileAd key="MatchTileAd" />;

  if (!Array.isArray(children)) {
    devWarn(`Children of MatchList should be an array of matches,
wrapping this in a containing element prevents us from manipulating the list.`);
  }

  const output = [...React.Children.toArray(children)];
  // todo
  const matchTileAdIndex = useGetMatchTileAdIndex({
    matchList,
    gameSymbol,
  } as unknown);
  if (matchTileAdIndex) {
    output.splice(matchTileAdIndex, 0, element);
  }

  return !hasLoaded ? (
    <MatchListLoading />
  ) : matchListError ? (
    <MatchListError />
  ) : !hasMatches ? (
    <MatchSeparatorTile Icon={GAME_BOX_ICONS[gameSymbol]} />
  ) : (
    output
  );
}

export const debouncedUpdateRoute = debounce(
  (
    pathname: string,
    searchParams: URLSearchParams,
    state: RouteState<Record<PrimitiveKey, unknown>>,
  ) => {
    // Qualify if we're still on the same route, because other route changes may have
    // occurred during the debounce window.
    if (
      decodeURIComponent(globals.location.pathname) !==
      rewritePathname(pathname)
    )
      return;

    updateRoute(pathname, searchParams, state).catch((error) => {
      devError("PROFILE UPDATE FAILED", error);
    });
  },
  50,
);

export const MatchTileWrapper = React.forwardRef<
  HTMLDivElement,
  MatchTileWrapperProps
>((props, ref) => {
  const { isLive } = props;
  return (
    <MatchTileContainer ref={ref} {...classNames(isLive && "live")}>
      <Match {...props} />
    </MatchTileContainer>
  );
});
MatchTileWrapper.displayName = "MatchTileWrapper";

export const MatchTile = React.memo(
  function MatchTile({
    children,
    id,
    match,
    matchRoute,
    isLive,
    height,
    handleMouseEvents,
  }: MatchTileProps) {
    const elementRef = useMemo(() => createRef<HTMLDivElement>(), []);
    const { shortName } = useGameInfo() ?? {};

    const { currentPath } = useRoute((prev, next) => {
      return prev.currentPath === next.currentPath;
    });

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          const {
            route: { currentPath, searchParams, state },
          } = router;
          for (const entry of entries) {
            const { isIntersecting } = entry;

            if (!state.transient?.visibleMatches?.[shortName]) {
              const visibleMatches = state.transient?.visibleMatches as
                | Record<string, unknown>
                | undefined;

              state.transient = {
                ...state.transient,
                visibleMatches: {
                  ...(visibleMatches ?? {}),
                  [shortName]: {},
                },
              };
            }

            state.transient.visibleMatches[shortName][id] = isIntersecting;
            debouncedUpdateRoute(currentPath, searchParams, state);
          }
        },
        {
          threshold: 0,
        },
      );

      observer.observe(elementRef.current);

      return () => {
        observer.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, elementRef.current, currentPath]);

    return (
      <MatchTileWrapper
        ref={elementRef}
        match={match}
        matchRoute={matchRoute}
        isLive={isLive}
        height={height}
        handleMouseEvents={handleMouseEvents}
      >
        {children}
      </MatchTileWrapper>
    );
  },
  // Removed advanced optimization because we've moved to MatchTilesV2 which now includes conditional logic for re-rendering

  // (prev, next) => {
  //   // Advanced optimization: don't try this at home, kids!
  //   // We are deliberately skipping `props.children`, assuming that
  //   // there's no conditional logic that would cause re-render here.
  //   for (const key in prev) {
  //     if (key === "children") continue;
  //     if (prev[key] !== next[key]) return false;
  //   }
  //   return true;
  // },
);

export function UnknownPlayerHeader() {
  const { t } = useTranslation();
  const icon = <Account />;
  return (
    <PageHeader
      icon={icon}
      title={t("common:error.generalAppError.title", "Oops")}
      style={{ gridArea: "header" }}
    />
  );
}

export function MatchesNotFound() {
  const { t } = useTranslation();
  const errorDescription = t("common:noMatchesFound", "No matches found");
  return (
    <MatchesNotFoundContainer>
      <ErrorComponent description={errorDescription} />
    </MatchesNotFoundContainer>
  );
}

export const MatchSeparatorTile = ({
  Icon,
  label,
}: MatchSeparatorTileProps) => {
  const { t } = useTranslation();
  return (
    <MatchSeparatorContainer>
      {Icon ? (
        <Icon />
      ) : (
        <p className={"new type-form--button"}>{t("common:new", "New")}</p>
      )}
      <div className={"right"}>
        <div>
          {label || t("common:welcomeToFeed", "Welcome to your Match Feed!")}
        </div>
        <div className={"caption type-body2"}>
          {t(
            "common:playSomeGames",
            "Play some games to start populating your feed and stats.",
          )}
        </div>
      </div>
    </MatchSeparatorContainer>
  );
};

export function ShowMoreFooter({ hasMore, setShowMore }: ShowMoreFooterProps) {
  const { t } = useTranslation();

  return (
    hasMore && (
      <MatchListFooter>
        <Button block onClick={setShowMore}>
          {t("common:more", "More")}
        </Button>
      </MatchListFooter>
    )
  );
}

function Profile({ children, ...props }: ProfileProps) {
  // todo
  const {
    props: { className: columnClass },
    // @ts-ignore
  } = useMemo(ProfileColumn, []);

  useLayoutEffect(() => {
    // For handling sticky sidebar + scrolling.
    const [root] = globals.document.getElementsByClassName(
      APP_SCROLLER,
    ) as HTMLCollectionOf<HTMLElement>;
    const [sidebar] = globals.document.getElementsByClassName(
      columnClass,
    ) as HTMLCollectionOf<HTMLElement>;
    if (!root || !sidebar) return;

    const updateOffset = () => {
      const offset = root.offsetHeight - sidebar.offsetHeight;
      sidebar.style.setProperty(
        "--y-offset",
        `min(var(--content-start), ${offset}px)`,
      );
    };
    const obs =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateOffset)
        : null;
    obs?.observe(root);
    obs?.observe(sidebar);
    updateOffset();
    return () => obs?.disconnect();
  }, [columnClass]);

  const containerRef = React.useRef(null);
  useEffect(() => {
    profileRefs.setupPlaceholder(containerRef.current);
  });

  return (
    <ProfileContainer {...props} ref={containerRef}>
      {children}
    </ProfileContainer>
  );
}

export default Profile;
